import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import SearchResultsAICard from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/searchResultsAICard';
import AIService from '@PROJECT_ROOT/assets/responsive-frontend/js/apiServices/AIService';
import { IoMdClose } from 'react-icons/io';
import { TbMessageCirclePlus, TbSend } from 'react-icons/tb';
import { getAdriaticLang } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import initAfterContentComponent from '@PROJECT_ROOT/assets/responsive-frontend/js/services/AfterContentComponent';
import {
    dispatchTrackingEvent,
    AI_SEARCH_NEW_CONVERSATION_CLICK,
    AI_SEARCH_RESULTS_ARROW_CLICK,
    AI_SEARCH_RESULTS_SCROLL,
    AI_SEARCH_CONTINUE_WITH_FILTERS_CLICK,
    AI_SEARCH_LINK_IN_TEXT_CLICK,
    AI_SEARCH_RESULTS_VIEW_ITEM_LIST,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import { IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';

function Modal({ show, onClose, url }) {
    const [frameLoading, setFrameLoading] = useState(true);

    useEffect(() => {
        if (show) {
            setFrameLoading(true);
        }
    }, [url, show]);

    if (!show) return null;

    return createPortal(
        <div
            className="modal-overlay"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
            }}
        >
            <button
                onClick={onClose}
                type="button"
                style={{
                    position: 'absolute',
                    top: '3%',
                    left: '4%',
                    background: '#F9F9F9',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    borderRadius: '50px',
                    zIndex: 10000,
                }}
            >
                <IoMdClose />
            </button>
            <div
                className="modal-content bg-white rounded-lg shadow-lg"
                style={{
                    width: '90%',
                    height: '90%',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {frameLoading && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 5000,
                        }}
                        className="frameLoadingSpinner"
                    >
                        <AiOutlineLoading3Quarters />
                    </div>
                )}
                <iframe
                    src={url}
                    title="Accommodation details"
                    onLoad={() => setFrameLoading(false)}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                    }}
                />
            </div>
        </div>,
        document.body,
    );
}

function AiSearchChat() {
    const sliderRefs = useRef({});
    const [, forceUpdate] = useState(0);

    const [messages, setMessages] = useState(() => {
        const saved = sessionStorage.getItem('ai_chat_messages');
        return saved ? JSON.parse(saved) : [];
    });
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [searchingOffers, setSearchingOffers] = useState(false);
    const [modalUrl, setModalUrl] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const messagesEndRef = useRef(null);
    const programmaticScrollRef = useRef({});
    const scrollTrackingTimerRef = useRef({});
    const [translations, setTranslations] = useState(null);
    const twoLetterISOLanguageCodeParameter = getAdriaticLang();

    // const conversationIdShownRef = useRef(false);

    useEffect(() => {
        if (!translations) return;
        const storedLang = sessionStorage.getItem('ai_chat_lang');

        if (storedLang !== twoLetterISOLanguageCodeParameter) {
            sessionStorage.removeItem('ai_chat_messages');
            sessionStorage.setItem('ai_chat_lang', twoLetterISOLanguageCodeParameter);

            setMessages([
                { role: 'ai', text: `👋 ${translations.data.helloMessage}` },
            ]);
        }
    }, [twoLetterISOLanguageCodeParameter, translations]);

    useEffect(() => {
        async function loadTranslations() {
            try {
                const data = await AIService.getAISearchFormData();
                setTranslations(data);
            } catch (err) {
                reportError(err);
            }
        }
        loadTranslations();
    }, []);

    const addMessage = (role, text, data = null) => {
        setMessages((prev) => [...prev, { role, text, data }]);
    };

    useEffect(() => {
        if (messages.length > 0) {
            sessionStorage.setItem('ai_chat_messages', JSON.stringify(messages));
        }
    }, [messages]);

    useEffect(() => {
        setTimeout(() => {
            forceUpdate((v) => v + 1);
        }, 100);
    }, [messages]);

    useEffect(() => {
        if (messages.length === 0) return;
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (!translations) return;

        setMessages((prev) => {
            if (prev.length === 0) {
                return [{ role: 'ai', text: `👋 ${translations.data.helloMessage}` }];
            }
            return prev;
        });
    }, [translations]);
    if (!translations) return <span className="loader" />;
    const handleOpenModal = (url) => {
        const newUrl = new URL(url, window.location.origin);
        newUrl.searchParams.set('utm_source', 'adriaticaisearch');
        newUrl.searchParams.set('utm_medium', 'internal');
        newUrl.searchParams.set('utm_campaign', 'ai_search');
        newUrl.searchParams.set('adriaticAI', '1');

        setModalUrl(newUrl.toString());
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setModalUrl(null);
    };

    const formatAdriaticUrls = (text) => {
        if (!text) return [text];

        const TOKEN_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)/gi;

        const isAdriaticUrl = (url) => /^https?:\/\/(www\.)?adriatic\.(hr|[a-z]{2,})/i.test(url);

        const stripTrailingPunctuation = (url) => url.replace(/[.,!?]+$/, '');

        const renderTextWithBreaks = (str, keyPrefix) => str.split('\n').reduce((acc, line, idx, arr) => {
            acc.push(<React.Fragment key={`${keyPrefix}-${idx}`}>{line}</React.Fragment>);
            if (idx < arr.length - 1) acc.push(<br key={`${keyPrefix}-br-${idx}`} />);
            return acc;
        }, []);

        const matches = Array.from(text.matchAll(TOKEN_REGEX));

        const { parts } = matches.reduce(
            (acc, match) => {
                const [fullMatch, mdText, mdUrl, bareUrl] = match;
                const offset = match.index;

                if (offset > acc.lastIndex) {
                    const before = text.slice(acc.lastIndex, offset);
                    acc.parts.push(...renderTextWithBreaks(before, `t-${offset}`));
                }

                const rawUrl = stripTrailingPunctuation(mdUrl || bareUrl);
                const displayText = mdText || rawUrl;

                if (isAdriaticUrl(rawUrl)) {
                    acc.parts.push(
                        <a
                            key={`a-${offset}`}
                            href={rawUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => { if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_LINK_IN_TEXT_CLICK); }}
                        >
                            {displayText}
                        </a>,
                    );
                } else {
                    acc.parts.push(<React.Fragment key={`u-${offset}`}>{rawUrl}</React.Fragment>);
                }

                return { parts: acc.parts, lastIndex: offset + fullMatch.length };
            },
            { parts: [], lastIndex: 0 },
        );

        const lastMatch = matches[matches.length - 1];
        const tail = lastMatch ? text.slice(lastMatch.index + lastMatch[0].length) : text;
        if (tail) {
            parts.push(...renderTextWithBreaks(tail, 'r'));
        }

        return parts;
    };

    const handleNewChat = () => {
        setMessages([{ role: 'ai', text: `👋 ${translations.data.helloMessage}` }]);
        sessionStorage.removeItem('ai_chat_messages');
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_NEW_CONVERSATION_CLICK);
    };

    const renderErrorTemplate = (template, params) => {
        const parts = [];
        let remaining = template.replace(/\r/g, '');

        Object.entries(params).forEach(([placeholder, { text, url }]) => {
            const idx = remaining.indexOf(placeholder);
            if (idx === -1) return;

            const before = remaining.slice(0, idx);
            const prefixMatch = before.match(/^([\s\S]*\n)(- )$/);

            if (prefixMatch) {
                parts.push({ type: 'text', content: prefixMatch[1] });
                parts.push({ type: 'prefix', content: prefixMatch[2] });
            } else {
                parts.push({ type: 'text', content: before });
            }

            parts.push({ type: 'link', text, url });
            remaining = remaining.slice(idx + placeholder.length);
        });

        parts.push({ type: 'text', content: remaining });
        return parts;
    };

    const handleLoadMore = async (msgIndex) => {
        const msg = messages[msgIndex];
        const { currentPage, apiParams, results } = msg.data;

        const nextPage = currentPage + 1;

        setLoading(true);

        const pagePath = `${nextPage}/${twoLetterISOLanguageCodeParameter}?`;

        let listResponse;
        try {
            listResponse = await AIService.getAISearchResultsData(
                pagePath,
                new URLSearchParams(apiParams).toString(),
            );
        } catch (e) {
            setLoading(false);
            return;
        }

        const newResults = listResponse.data.results || [];

        const updatedData = {
            ...msg.data,
            results: [...results, ...newResults],
            currentPage: nextPage,
        };

        setMessages((prev) => {
            const copy = [...prev];
            copy[msgIndex] = { ...copy[msgIndex], data: updatedData };
            return copy;
        });
        forceUpdate((v) => v + 1);
        setLoading(false);
    };

    const getHorizontalPadding = (el) => {
        if (!el) return 0;
        const style = window.getComputedStyle(el);
        return parseInt(style.paddingLeft, 10) || 0;
    };

    const SLIDE_STEP = 330;

    const slideLeft = (index) => {
        const el = sliderRefs.current[index];
        if (!el) return;

        const paddingLeft = getHorizontalPadding(el);

        const target = el.scrollLeft <= paddingLeft ? 0 : el.scrollLeft - SLIDE_STEP;
        programmaticScrollRef.current[index] = true;
        el.scrollTo({ left: target, behavior: 'smooth' });
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_RESULTS_ARROW_CLICK);
    };

    const slideRight = (index) => {
        const el = sliderRefs.current[index];
        if (!el) return;

        const paddingLeft = getHorizontalPadding(el);

        const target = el.scrollLeft === 0 ? SLIDE_STEP + paddingLeft : el.scrollLeft + SLIDE_STEP;

        el.scrollTo({ left: target, behavior: 'smooth' });
        if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_RESULTS_ARROW_CLICK);
    };

    const canScrollLeft = (el) => {
        if (!el) return false;
        const paddingLeft = getHorizontalPadding(el);
        return el.scrollLeft > paddingLeft;
    };

    const canScrollRight = (el) => {
        if (!el) return true;
        return el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    };

    const handleSend = async () => {
        if (!query.trim() || loading) return;

        const userMessage = query.trim();
        addMessage('user', userMessage);
        setQuery('');
        setLoading(true);
        const lastMessage = userMessage;
        const isFirstUserMessage = messages.filter((m) => m.role === 'user').length === 0;
        const askAIPath = await AIService.postAIPath({
            message: lastMessage,
            freshStartPlease: isFirstUserMessage ? 1 : 0,
        });

        if (askAIPath.error) {
            addMessage('ai', `❌ ${askAIPath.error}`);
            setLoading(false);
            return;
        }

        if (askAIPath.data.errorMessageTemplate) {
            const parts = renderErrorTemplate(
                askAIPath.data.errorMessageTemplate,
                askAIPath.data.errorMessageTemplateParameters,
            );
            addMessage('ai', null, { type: 'errorTemplate', parts });
            setLoading(false);
            return;
        }

        const aiMessage = askAIPath.data.chat_message?.trim();

        if (askAIPath.data.isAdriaticRelated === false) {
            if (aiMessage) {
                addMessage('ai', aiMessage);
            } else {
                addMessage('ai', `🤔 ${translations.data.wrongContextMessage}`);
            }

            setLoading(false);
            return;
        }

        if (aiMessage) {
            addMessage('ai', aiMessage);
        }

        const hasSearchParams = Boolean(
            askAIPath.data.locationId || askAIPath.data.from || askAIPath.data.to || askAIPath.data.persons_adult || askAIPath.data.persons_0_3
            || askAIPath.data.persons_4_11 || askAIPath.data.persons_12_17 || (askAIPath.data.filters && Object.keys(askAIPath.data.filters).length > 0),
        );

        if (!hasSearchParams) {
            setLoading(false);
            return;
        }

        const supportedLocales = [
            'en', 'hr', 'cs', 'de', 'fr', 'it', 'hu', 'pl', 'ro', 'ru', 'sk', 'sl', 'es', 'nl', 'uk', 'da', 'sv', 'no',
        ];
        let languageParameter = twoLetterISOLanguageCodeParameter;
        let translationsToUse = translations;
        if (supportedLocales.includes(askAIPath.data.customerQuestionTwoLetterISOLanguageCode)) {
            languageParameter = askAIPath.data.customerQuestionTwoLetterISOLanguageCode;
            try {
                const data = await AIService.getAISearchFormData(languageParameter);
                setTranslations(data);
                translationsToUse = data;
            } catch (err) {
                reportError(err);
            }
        }
        const apiParams = {
            ...askAIPath.data.filters,
            location_id: askAIPath.data.locationId,
            from: askAIPath.data.from,
            to: askAIPath.data.to,
            persons_adult: askAIPath.data.persons_adult,
            persons_0_3: askAIPath.data.persons_0_3,
            persons_4_11: askAIPath.data.persons_4_11,
            persons_12_17: askAIPath.data.persons_12_17,
            sort: askAIPath.data.sort || 'price',
        };

        try {
            const listResponse = await AIService.getAISearchResultsData(`1/${languageParameter}?`, new URLSearchParams(apiParams).toString());
            const listData = listResponse.data;
            if (listData.results?.length) {
                const total = parseInt(listData.totalResults.split(':')[1].trim(), 10);
                addMessage('ai', `✅ ${translationsToUse.data.numberOfResultsMessage} ${total}`, {
                    results: listData.results,
                    total,
                    currentPage: 1,
                    pageSize: 12,
                    apiParams,
                    nearbyLocationText: listData.nearbyLocationText,
                    showAllUnitsText: listData.showAllUnitsText,
                    showLessUnitsText: listData.showLessUnitsText,
                    makeReservationText: listData.makeReservationText,
                    checkAvailabilityText: listData.checkAvailabilityText,
                });
                if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_RESULTS_VIEW_ITEM_LIST);
            } else {
                addMessage('ai', `😕 ${translationsToUse.data.noResultsMessage}`);
            }
        } catch (e) {
            addMessage('ai', `⚠️ ${translationsToUse.data.problemMessage}`);
        } finally {
            setSearchingOffers(false);
            setLoading(false);
        }
        if (askAIPath.data.ai_suggestion_message && askAIPath.data.standardSearchAlternativeTranslation) {
            addMessage('ai', null, {
                type: 'suggestion',
                suggestionMessage: askAIPath.data.ai_suggestion_message || null,
                alternativeText: askAIPath.data.standardSearchAlternativeTranslation || null,
                alternativeURL: askAIPath.data.standardSearchURLAlternative || null,
            });
        } else if (askAIPath.data.standardSearchAlternativeTranslation) {
            addMessage('ai', null, {
                type: 'suggestion',
                alternativeText: askAIPath.data.standardSearchAlternativeTranslation,
                alternativeURL: askAIPath.data.standardSearchURLAlternative || null,
            });
        }

        setLoading(false);
    };

    return (
        <div className="container">
            <div className="container__inner">
                <div className="card-body">
                    {messages.map((msg, i) => {
                        const firstNearbyIndex = Array.isArray(msg.data?.results)
                            ? msg.data.results.findIndex((r) => r.isNearbyLocation)
                            : -1;
                        return (
                            <div key={i} className={`d-flex mb-3 ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                                <div
                                    className={`p-3 rounded ${msg.role === 'user' ? 'bg-primary text-white ml-5' : 'bg-white border mr-2 mr-md-5 AISearchResults__Container'}`}
                                    style={{ maxWidth: '100%' }}
                                >
                                    <span>
                                        {typeof msg.text === 'string' && formatAdriaticUrls(msg.text).map((part, idx) => (
                                            <React.Fragment key={idx}>
                                                {part}
                                            </React.Fragment>
                                        ))}
                                        {msg.data?.type === 'suggestion' && msg.data.alternativeURL && (
                                            <div>
                                                {msg.data.suggestionMessage && <>💡 {msg.data.suggestionMessage} </>}
                                                <a
                                                    href={msg.data.alternativeURL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => { if (!IS_FRAME_SERVICE) dispatchTrackingEvent(AI_SEARCH_CONTINUE_WITH_FILTERS_CLICK); }}
                                                >
                                                    {msg.data.alternativeText}
                                                </a>
                                            </div>
                                        )}
                                        {msg.data?.type === 'errorTemplate' && msg.data.parts && (
                                            <span>
                                                {msg.data.parts.map((part, idx) => {
                                                    if (part.type === 'link') {
                                                        return (
                                                            <a key={idx} href={part.url} target="_blank" rel="noopener noreferrer">
                                                                {part.text}
                                                            </a>
                                                        );
                                                    }
                                                    if (part.type === 'prefix') {
                                                        return <React.Fragment key={idx}>{part.content}</React.Fragment>;
                                                    }
                                                    return (
                                                        <React.Fragment key={idx}>
                                                            {part.content.split('\n').map((line, li, arr) => (
                                                                <React.Fragment key={li}>
                                                                    {line}
                                                                    {li < arr.length - 1 && <br />}
                                                                </React.Fragment>
                                                            ))}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </span>
                                        )}
                                    </span>
                                    {Array.isArray(msg.data?.results) && msg.data.results.length > 0 && (
                                        <div className="mt-3">
                                            <div className="row AISearchResults__slider" id="AISearchResults__slider">
                                                <div className="AISearchResults__controls">
                                                    <button
                                                        type="button"
                                                        className={`btn btn-light ${!canScrollLeft(sliderRefs.current[i]) ? 'invisible' : ''}`}
                                                        onClick={() => slideLeft(i)}
                                                    >
                                                        ‹
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className={`btn btn-light ${!canScrollRight(sliderRefs.current[i]) ? 'invisible' : ''}`}
                                                        onClick={() => slideRight(i)}
                                                    >
                                                        ›
                                                    </button>
                                                </div>

                                                <div
                                                    className="col-12 AISearchResults__wrapper"
                                                    ref={(el) => {
                                                        if (!el) return;
                                                        sliderRefs.current[i] = el;

                                                        el.onscroll = () => {
                                                            forceUpdate((v) => v + 1);
                                                            if (programmaticScrollRef.current[i]) {
                                                                programmaticScrollRef.current[i] = false;
                                                                return;
                                                            }

                                                            if (!IS_FRAME_SERVICE) {
                                                                clearTimeout(scrollTrackingTimerRef.current[i]);
                                                                scrollTrackingTimerRef.current[i] = setTimeout(() => {
                                                                    dispatchTrackingEvent(AI_SEARCH_RESULTS_SCROLL);
                                                                }, 300);
                                                            }
                                                        };
                                                    }}
                                                >
                                                    {Array.isArray(msg.data?.results) && msg.data.results.map((r, z) => (
                                                        <React.Fragment key={z}>
                                                            {z === firstNearbyIndex && firstNearbyIndex !== -1 && (
                                                                <div className="search-results__nearby-location-alert">
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="24"
                                                                        height="24"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C12.9333 20 13.8125 19.8542 14.6375 19.5625C15.4625 19.2708 16.2167 18.8583 16.9 18.325L15.475 16.9C14.9917 17.25 14.4542 17.5208 13.8625 17.7125C13.2708 17.9042 12.65 18 12 18C10.3333 18 8.91667 17.4167 7.75 16.25C6.58333 15.0833 6 13.6667 6 12C6 10.3333 6.58333 8.91667 7.75 7.75C8.91667 6.58333 10.3333 6 12 6C13.6667 6 15.0833 6.58333 16.25 7.75C17.4167 8.91667 18 10.3333 18 12C18 12.65 17.9 13.275 17.7 13.875C17.5 14.475 17.225 15.0167 16.875 15.5L18.3 16.925C18.8333 16.2417 19.25 15.4833 19.55 14.65C19.85 13.8167 20 12.9333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20ZM12 16C12.3667 16 12.7208 15.9542 13.0625 15.8625C13.4042 15.7708 13.725 15.6333 14.025 15.45L12.5 13.925C12.4167 13.9583 12.3333 13.9792 12.25 13.9875C12.1667 13.9958 12.0833 14 12 14C11.45 14 10.9792 13.8042 10.5875 13.4125C10.1958 13.0208 10 12.55 10 12C10 11.45 10.1958 10.9792 10.5875 10.5875C10.9792 10.1958 11.45 10 12 10C12.55 10 13.0208 10.1958 13.4125 10.5875C13.8042 10.9792 14 11.45 14 12C14 12.1 13.9958 12.1958 13.9875 12.2875C13.9792 12.3792 13.9583 12.4667 13.925 12.55L15.425 14.05C15.6083 13.75 15.75 13.4292 15.85 13.0875C15.95 12.7458 16 12.3833 16 12C16 10.9 15.6083 9.95833 14.825 9.175C14.0417 8.39167 13.1 8 12 8C10.9 8 9.95833 8.39167 9.175 9.175C8.39167 9.95833 8 10.9 8 12C8 13.1 8.39167 14.0417 9.175 14.825C9.95833 15.6083 10.9 16 12 16Z"
                                                                            fill="#0574B0"
                                                                        />
                                                                    </svg>
                                                                    <span>{msg.data.nearbyLocationText}</span>
                                                                </div>
                                                            )}

                                                            <div>
                                                                <SearchResultsAICard
                                                                    data={{
                                                                        ...r,
                                                                        showAllUnitsText: msg.data.showAllUnitsText || '',
                                                                        showLessUnitsText: msg.data.showLessUnitsText || '',
                                                                        makeReservationText: msg.data.makeReservationText || '',
                                                                        checkAvailabilityText: msg.data.checkAvailabilityText || '',
                                                                    }}
                                                                    isDateRangeSelected={false}
                                                                    firstCarouselImageLazy={true}
                                                                    wasFirstResult={1}
                                                                    page={1}
                                                                    intervalFilterWasUsed={false}
                                                                    onOpenModal={handleOpenModal}
                                                                />
                                                            </div>
                                                        </React.Fragment>
                                                    ))}

                                                    {msg.data.results.length < msg.data.total && (() => {
                                                        const itemsLeft = msg.data.total - msg.data.results.length;
                                                        return (
                                                            <div className="text-center mt-3 AISearchResults__loadMore">
                                                                <button
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => handleLoadMore(i)}
                                                                    type="button"
                                                                >
                                                                    +{itemsLeft}
                                                                </button>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {loading && (
                        <div className="d-flex justify-content-start mb-3">
                            <div className="bg-white border rounded px-3 py-2 text-muted">
                                {searchingOffers ? translations.data.lookingForOfferMessage : translations.data.thinkingNowMessage}
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>
                <div className="card-footer">
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder={translations.data.useAIMessage}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        />
                        <div className="input-group-append">
                            <button className="btn btn-primary" onClick={handleSend} disabled={loading} type="button" title={translations.data.sendText}>
                                {loading ? '...' : <TbSend />}
                            </button>
                            <button className="btn btn-sm btn-secondary btn-newChat" onClick={handleNewChat} type="button" title={translations.data.newChatText} disabled={loading}>
                                <TbMessageCirclePlus />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Modal show={isModalOpen} url={modalUrl} onClose={handleCloseModal} />
        </div>
    );
}

const aiSearchReact = document.getElementById('aiSearchReact');
const root = createRoot(aiSearchReact);
root.render(<AiSearchChat />);
initAfterContentComponent();
